// The patient portal's pure rules (docs/plans/phase3-portal-feedback-kiswahili.md §4, §6;
// ADR 0035): sign-in codes and their limits, sessions, what a patient may see, and when a
// guardian may see a child. Every number here is a provisional owner decision (plan §11);
// every exposure rule is TODO(clinical-verify).

import { smsFacilityName, SMS_MAX_LENGTH } from '../appointments/reminders.ts';
import type { LabInterpretation } from '../lab/flags.ts';
import type { LabReportStatus } from '../lab/report-status.ts';
import { completedMonths, daysBetween, type CalendarDay } from '../time.ts';

const MINUTE_MS = 60_000;
const DAY_MS = 24 * 60 * MINUTE_MS;

// --- Sign-in codes (plan §4.1, P2) -----------------------------------------------------

export const PORTAL_CODE_POLICY = {
  digits: 6,
  /** A code is good for five minutes. */
  ttlSeconds: 300,
  /** Wrong tries allowed on one code. */
  maxAttempts: 5,
  /** The sign-in page offers a new code after this long. */
  resendAfterSeconds: 30,
  /** Codes per number in the short window, and in a day. Decoys count too. */
  perNumberWindowMinutes: 15,
  perNumberInWindow: 3,
  perNumberPerDay: 10,
  /** Wrong codes, across challenges, before sign-in locks; then 15 minutes doubling. */
  lockThreshold: 5,
  lockMinutes: 15,
  maxLockMinutes: 24 * 60,
} as const;

const CODE_SPACE = 10 ** PORTAL_CODE_POLICY.digits;

/** A six-digit code from `randomInt(max)` (crypto.randomInt on the server). */
export function newPortalCode(randomInt: (max: number) => number): string {
  const n = randomInt(CODE_SPACE);
  if (!Number.isSafeInteger(n) || n < 0 || n >= CODE_SPACE) {
    throw new RangeError('The random source returned a number outside the code space');
  }
  return String(n).padStart(PORTAL_CODE_POLICY.digits, '0');
}

export function isPortalCode(value: string): boolean {
  return /^\d{6}$/.test(value);
}

export type CodeRequestDecision = { allowed: true } | { allowed: false; retryAfterSeconds: number };

/**
 * Whether another code may be sent to a number, given when earlier codes were asked for
 * (decoys included, so the answer says nothing about whether the number has an account).
 */
export function codeRequestDecision(previous: readonly Date[], now: Date): CodeRequestDecision {
  const t = now.getTime();
  const dayAgo = t - DAY_MS;
  const windowMs = PORTAL_CODE_POLICY.perNumberWindowMinutes * MINUTE_MS;
  const today = previous
    .map((d) => d.getTime())
    .filter((x) => x > dayAgo && x <= t)
    .sort();
  if (today.length >= PORTAL_CODE_POLICY.perNumberPerDay) {
    const oldest = today[today.length - PORTAL_CODE_POLICY.perNumberPerDay] ?? t;
    return { allowed: false, retryAfterSeconds: Math.ceil((oldest + DAY_MS - t) / 1000) };
  }
  const recent = today.filter((x) => x > t - windowMs);
  if (recent.length >= PORTAL_CODE_POLICY.perNumberInWindow) {
    const oldest = recent[recent.length - PORTAL_CODE_POLICY.perNumberInWindow] ?? t;
    return { allowed: false, retryAfterSeconds: Math.ceil((oldest + windowMs - t) / 1000) };
  }
  return { allowed: true };
}

/** Minutes sign-in stays locked after this many wrong codes in a row, or null. */
export function portalLockMinutes(failures: number): number | null {
  const { lockThreshold, lockMinutes, maxLockMinutes } = PORTAL_CODE_POLICY;
  if (failures < lockThreshold) return null;
  const runs = Math.floor(failures / lockThreshold) - 1;
  return Math.min(lockMinutes * 2 ** runs, maxLockMinutes);
}

// --- Sessions (plan §4.1, P3) ------------------------------------------------------------

export const PORTAL_SESSION_POLICY = { idleMinutes: 15, absoluteHours: 4 } as const;

export type PortalSessionState = 'live' | 'idle' | 'expired';

export function portalSessionState(
  session: { lastSeenAt: Date; expiresAt: Date },
  now: Date,
): PortalSessionState {
  if (now.getTime() >= session.expiresAt.getTime()) return 'expired';
  const idle = now.getTime() - session.lastSeenAt.getTime();
  return idle >= PORTAL_SESSION_POLICY.idleMinutes * MINUTE_MS ? 'idle' : 'live';
}

// --- What the portal shows (plan §4.2) ---------------------------------------------------

/** E1: the portal is cleared for no sensitivity label, for any patient or guardian. */
export function portalMayShowVisit(labels: readonly string[]): boolean {
  return labels.length === 0;
}

const CRITICAL: ReadonlySet<LabInterpretation> = new Set([
  'critical_low',
  'critical_high',
  'critical',
]);

/**
 * E1–E3: final (released after validation), unlabelled, and with no critical result. A
 * culture the lab marked critical counts as a critical result (lab QC and microbiology plan
 * Q12); preliminary culture reports never reach this check, as they are never final.
 */
export function portalMayShowLabReport(report: {
  status: LabReportStatus;
  visitLabels: readonly string[];
  interpretations: readonly LabInterpretation[];
  cultureCritical?: boolean;
}): boolean {
  if (report.status !== 'final') return false;
  if (!portalMayShowVisit(report.visitLabels)) return false;
  if (report.cultureCritical === true) return false;
  return !report.interpretations.some((i) => CRITICAL.has(i));
}

/** E4: a written result may hold clinical notes, so it is withheld; "not done" says only that. */
export function portalResultDisplay(result: {
  resultType: 'numeric' | 'coded' | 'text';
  display: string;
  notDone: boolean;
}): { display: string; withheld: boolean } {
  if (result.notDone) return { display: 'Not done', withheld: false };
  if (result.resultType === 'text') return { display: '', withheld: true };
  return { display: result.display, withheld: false };
}

// --- Dependants (plan §4.3, P6) ----------------------------------------------------------

export const PORTAL_DEPENDANT_MAX_AGE_YEARS = 18;

export type DependantHiddenReason =
  'link_ended' | 'relationship_ended' | 'adult' | 'deceased' | 'no_birth_date';

export function dependantVisibility(input: {
  linkActive: boolean;
  relationshipActive: boolean;
  /** The relationship marks the child a dependant of the account holder. */
  isDependantOfHolder: boolean;
  birthDate: CalendarDay | null;
  deceased: boolean;
  today: CalendarDay;
}): { visible: true } | { visible: false; reason: DependantHiddenReason } {
  if (!input.linkActive) return { visible: false, reason: 'link_ended' };
  if (!input.relationshipActive || !input.isDependantOfHolder)
    return { visible: false, reason: 'relationship_ended' };
  if (input.deceased) return { visible: false, reason: 'deceased' };
  if (input.birthDate === null) return { visible: false, reason: 'no_birth_date' };
  if (daysBetween(input.birthDate, input.today) < 0)
    return { visible: false, reason: 'no_birth_date' };
  const years = Math.floor(completedMonths(input.birthDate, input.today) / 12);
  if (years >= PORTAL_DEPENDANT_MAX_AGE_YEARS) return { visible: false, reason: 'adult' };
  return { visible: true };
}

// --- SMS (plan §4.1, §5.1) ---------------------------------------------------------------

/**
 * The sign-in code SMS: the facility and the code only, never the patient's name. English
 * for now (plan P15, TODO(language-verify)).
 */
export function portalCodeSmsText(input: { facilityName: string; code: string }): string {
  if (!isPortalCode(input.code)) throw new RangeError('Not a portal code');
  return `Your ${smsFacilityName(input.facilityName)} portal code is ${input.code}. It expires in 5 minutes. Do not share it.`;
}

/**
 * The feedback request SMS. The link carries a single-use token that reveals nothing by
 * itself; the facility name is shortened until the message fits one SMS.
 */
export function feedbackRequestSmsText(input: { facilityName: string; link: string }): string {
  const build = (name: string) => `${name} would like your feedback on your visit: ${input.link}`;
  let name = smsFacilityName(input.facilityName);
  while (build(name).length > SMS_MAX_LENGTH && name.length > 0) {
    name = name.slice(0, -1).trim();
  }
  const text = build(name === '' ? 'Your clinic' : name);
  if (text.length > SMS_MAX_LENGTH)
    throw new RangeError('The feedback link is too long for one SMS');
  return text;
}
